import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { sortFundNames } from '../../../../../utilities/customSorting';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import VersionSelector from './VersionSelector';
import { IconButton } from './buttons';
import { getFilteredRowModel } from '@tanstack/react-table';
import Pill from '../../../../ui/Pill';
import CreateKidDocumentModal from './CreateKidDocumentModal';
import SearchBar from '../../../../ui/inputs/SearchBar';
import { RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import { getReviewVersionParams } from '../utils/reviewNavigation';
import {
  isShareClassBlockedByTemplateIncomplete,
  TEMPLATE_INCOMPLETE_ACTION_TOOLTIP,
} from '../utils/templateIncomplete.utils';
import { useNavigate } from 'react-router-dom';
import FiguresAlert from '../../kidsV2/components/FiguresAlert';
import PortalTools from '../../../../tables/ultraTable/components/buttons/PortalTools';
import TokenService from '../../../../../services/token-service';
import {
  useDeactivateKidDocument,
  usePublishKidDocument,
  useReactivateKidDocument,
} from '../../../../../api/kids/hooks/mutations';
import { KidDocumentIdentifierPayload } from '../../../../../api/kids/types';
import useConfirmation from '../../../../../hooks/useConfirmation';
import { useGetKidOverviewHistoricalData } from '../../../../../api/kids/hooks/queries';
import { Tooltip } from '@mui/material';
import CompareVersionSelector from './CompareVersionSelector';
import { getLatestMajorPublishedVersion } from '../../../../../api/kids/kids.routes';
import { sanitiseFileName } from '../../../../../utilities/helpers/common/sanitisers';

import type { KidDocumentForm } from '../../kidsV2/types/KidPdfGeneratorV2.types';
import { UpdatedKidDataModel } from '../../../../../api/kids/types';
import type { ColumnAlignMeta } from '../../../../tables/tanstack/tanstackUtils';
import {
  KidDocumentStatus,
  KidFund,
  KidShareClass,
  KID_STATUS_LABEL,
  KID_STATUS_TO_PILL,
} from '../types/KidEditorV2.types';
import { useGenerateKidDocument } from '../../../../../api/kids/hooks/mutations';
import { transformKidDataModelsToElements } from '../utils/kidDataTransformers';

interface ShareClassTableRowProps {
  fund: KidFund;
  colSpan: number;
  selectedJurisdiction: string;
  selectedLanguage: string;
  highlightShareClassId?: string;
}

export const ShareClassTableRow: FunctionComponent<ShareClassTableRowProps> = ({
  fund,
  colSpan,
  selectedJurisdiction,
  selectedLanguage,
  highlightShareClassId,
}) => {
  const navigate = useNavigate();
  const { user_client_id: clientId } = TokenService.getUser()!;
  // Table filter states
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // Modal state for Create KID Document
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedShareClass, setSelectedShareClass] =
    useState<KidShareClass | null>(null);

  const deactivateKidDocumentMutation = useDeactivateKidDocument();
  const publishKidDocumentMutation = usePublishKidDocument();
  const reactivateKidDocumentMutation = useReactivateKidDocument();
  const { handleConfirmation } = useConfirmation();
  const generateKidDocumentMutation = useGenerateKidDocument();


  const data = useMemo(() => {
    const filtered = fund.shareClasses
      .filter((sc) => (showDeactivated ? true : sc.kid_is_active))
      .map((sc) => ({ ...sc, fundID: fund.fund_id }));

    const sortByName = (a: typeof filtered[0], b: typeof filtered[0]) =>
      sortFundNames(
        a.share_class_name_full || a.share_class_name || a.share_class_id,
        b.share_class_name_full || b.share_class_name || b.share_class_id,
      );

    const withDoc = filtered.filter((sc) => sc.status !== KidDocumentStatus.NoDocument).sort(sortByName);
    const noDoc = filtered.filter((sc) => sc.status === KidDocumentStatus.NoDocument).sort(sortByName);

    return [...withDoc, ...noDoc];
  }, [fund.shareClasses, showDeactivated, fund.fund_id]);

  const fetchLatestPublishedVersionInformation = useCallback(
    async (shareClass: KidShareClass) => {
      try {
        // Fetch the latest published version data
        const { data: latestPublished, version } = await getLatestMajorPublishedVersion(
          clientId,
          fund.fund_id,
          shareClass.share_class_id,
        );

        if (!latestPublished) {
          console.error('No published version found');
          return;
        }

        // Extract kid data models - handle both array and object formats
        const kidDataModels: UpdatedKidDataModel[] =
          (latestPublished as any).raw_kid_data_models ||
          (latestPublished as any).kid_content ||
          (latestPublished as any).kidContent ||
          (latestPublished as any).kid_data_models ||
          (latestPublished as any).kidDataModels ||
          (Array.isArray(latestPublished) ? latestPublished : []);

        if (!kidDataModels || kidDataModels.length === 0) {
          console.error('No kid data models found in published version');
          return;
        }

        // Transform UpdatedKidDataModel[] to PdfElement[]
        const elements = transformKidDataModelsToElements(kidDataModels);

        // Filter out table elements before sending to PDF generator

        // Create KidDocumentForm payload
        const payload: KidDocumentForm = {
          client: clientId,
          fund_name: fund.fund_name,
          fund_name_full: fund.fund_name_full,
          share_class:
            shareClass.share_class_name_full ||
            shareClass.share_class_name ||
            shareClass.share_class_id,
          date: kidDataModels[0].last_updating_timestamp,
          elements,
        };

        // Generate PDF
        const response = await generateKidDocumentMutation.mutateAsync(payload);

        // Create blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${[
          sanitiseFileName(fund.fund_name_full, 'fund'),
          sanitiseFileName(
            shareClass.share_class_name_full || shareClass.share_class_name,
            shareClass.share_class_id,
          ),
        ].join('-')}-${version}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error fetching and downloading latest published version:', error);
      }
    },
    [clientId, fund.fund_id, fund.fund_name, fund.fund_name_full, generateKidDocumentMutation],
  );

  const columns = useMemo<ColumnDef<KidShareClass & { fundID: string }, any>[]>(
    () => [
      {
        accessorKey: 'share_class_id',
        header: 'Share Class Name',
        meta: { align: 'left' } as any,
        cell: ({ row }) => {
          const shareClass = row.original as KidShareClass;
          const hasAlert = shareClass.has_alert;
          const hasTemplateSync = shareClass.has_shareclass_out_of_sync;
          const displayName = shareClass.share_class_name_full || shareClass.share_class_name || shareClass.share_class_id;
          return (
            <div className="flex items-center gap-1.5">
              <span className="font-medium">{displayName}</span>
              {hasAlert && <FiguresAlert />}
              {hasTemplateSync && (
                <FiguresAlert variant="template-sync" />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'share_class_isin',
        header: 'ISIN',
        cell: ({ getValue }) => (
          <span className="font-mono text-sm">{getValue()}</span>
        ),
      },
      {
        accessorKey: 'version_number',
        header: 'Version',
        cell: ({ getValue }) => {
          const version = getValue();
          if (version === '-')
            return <span className="text-gray-500">{version}</span>;
          const colorMap: Record<string, string> = {
            '6.2.9': 'text-blue-600',
            '5.1.2': 'text-orange-600',
            '4.3.1': 'text-red-600',
          };
          return (
            <span
              className={`font-medium ${colorMap[version] || 'text-gray-600'}`}
            >
              {version}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableColumnFilter: true, // allows dropdown filtering
        cell: ({ row, getValue }) => {
          const status = getValue() as KidShareClass['status'];
          const shareClass = row.original as KidShareClass;
          const hasAlert = shareClass.has_alert;
          const hasTemplateSync = shareClass.has_shareclass_out_of_sync;
          const pillContent = (
            <Pill status={(KID_STATUS_TO_PILL[status] as any) || 'neutral'} className="text-[12px]">
              {KID_STATUS_LABEL[status] || status}
            </Pill>
          );

          return (
            <div className="relative">
              {status === KidDocumentStatus.Rejected && shareClass.description ? (
                <Tooltip
                  title={shareClass.description}
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      className:
                        'bg-white text-gray-700 p-2.5 border border-gray-200 rounded-lg shadow-sm text-sm max-w-xs',
                    },
                    arrow: {
                      sx: {
                        color: 'white',
                        '&::before': {
                          border: '1px solid #e5e7eb',
                          boxSizing: 'border-box',
                        },
                      },
                    },
                  }}
                >
                  <span className="cursor-help">{pillContent}</span>
                </Tooltip>
              ) : (
                pillContent
              )}
              {(hasAlert || hasTemplateSync) && (
                <span className="absolute -right-1 -top-1 flex gap-0.5">
                  {hasAlert && <FiguresAlert className="h-4 w-4" />}
                  {hasTemplateSync && (
                    <FiguresAlert variant="template-sync" className="h-4 w-4" />
                  )}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'view',
        header: 'View',
        cell: ({ row }) => {
          const shareClass = row.original as KidShareClass;
          return (
            <VersionSelector
              shareClass={shareClass}
              fundId={fund.fund_id}
              clientId={clientId}
            />
          );
        },
      },
      {
        id: 'compare',
        header: 'Compare',
        cell: ({ row }) => {
          const shareClass = row.original as KidShareClass;
          // Don't show compare selector for no-document or deactivated status
          if (
            shareClass.status === KidDocumentStatus.NoDocument ||
            shareClass.status === KidDocumentStatus.Deactivated
          ) {
            return <span>-</span>;
          }
          return (
            <CompareVersionSelector
              shareClass={row.original}
              clientId={clientId}
              fundId={fund.fund_id}
            />
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const { status, kid_is_active } = row.original;
          const shareClass = row.original as KidShareClass;

          const renderButtons = () => {
            // If share class is not active, treat it as deactivated
            const isDeactivated = status === KidDocumentStatus.Deactivated || !kid_is_active;
            const isNoDocument = status === KidDocumentStatus.NoDocument;
            // Editing allowed when in explicit editing state, edited (ready for review), reviewed (ready to publish), published, or rejected
            const isEdit =
              status === KidDocumentStatus.Editing ||
              status === KidDocumentStatus.Edited ||
              status === KidDocumentStatus.Reviewed ||
              status === KidDocumentStatus.Published ||
              status === KidDocumentStatus.Rejected;
            const isReview =
              status === KidDocumentStatus.Edited &&
              !isShareClassBlockedByTemplateIncomplete(shareClass);
            const isPublish =
              status === KidDocumentStatus.Reviewed &&
              !isShareClassBlockedByTemplateIncomplete(shareClass);
            const isTemplateBlocked =
              isShareClassBlockedByTemplateIncomplete(shareClass);

            if (isDeactivated)
              return (
                <>
                  <IconButton
                    variant="reactivate"
                    tooltip="Reactivate Document"
                    onClick={() => {
                      handleConfirmation(
                        'Reactivate Document',
                        <>
                          <strong>
                            Are you sure you want to reactivate this KID
                            document?
                          </strong>
                          <p>
                            This action will reactivate the KID document for
                            share class {row.original.share_class_name_full || row.original.share_class_name || row.original.share_class_id}. The document will
                            become active and available again.
                          </p>
                        </>,
                        () => {
                          const payload = {
                            client_id: clientId,
                            fund_id: fund.fund_id,
                            share_class_id: row.original.share_class_id,
                            document_language: row.original.document_language,
                            message: 'Reactivated Document from FRONTEND',
                          };

                          reactivateKidDocumentMutation.mutateAsync(payload);
                        },
                      );
                    }}
                  />
                  <IconButton
                    variant="download"
                    tooltip="Download latest Published"
                    onClick={() => fetchLatestPublishedVersionInformation(row.original)}
                  />
                </>
              );
            if (isNoDocument)
              return (
                <>
                  <IconButton
                    variant="create"
                    onClick={() => {
                      setSelectedShareClass(row.original as KidShareClass);
                      setCreateOpen(true);
                    }}
                    tooltip="Create KID Document"
                  />
                  <IconButton
                    variant="download"
                    tooltip="Download latest Published"
                    disabled
                  />
                </>
              );

            return (
              <>
                <IconButton
                  variant="edit"
                  onClick={() => {
                    const shareClass = row.original as KidShareClass;
                    navigate(
                      `${RaidrV2RoutePaths.KID_EDITOR_V2}?fundId=${fund.fund_id}&shareClassId=${shareClass.share_class_id}`,
                    );
                  }}
                  disabled={!isEdit}
                  hasNotification={isEdit}
                  tooltip="Edit Document"
                />
                <ReviewButton
                  shareClass={shareClass}
                  fundId={fund.fund_id}
                  clientId={clientId}
                  disabled={!isReview}
                  hasNotification={isReview}
                  tooltip={
                    isTemplateBlocked &&
                    status === KidDocumentStatus.Edited
                      ? TEMPLATE_INCOMPLETE_ACTION_TOOLTIP
                      : 'Review Document'
                  }
                />
                <IconButton
                  variant="publish"
                  disabled={!isPublish}
                  hasNotification={isPublish}
                  tooltip={
                    isTemplateBlocked &&
                    status === KidDocumentStatus.Reviewed
                      ? TEMPLATE_INCOMPLETE_ACTION_TOOLTIP
                      : 'Publish Document'
                  }
                  onClick={() => {
                    handleConfirmation(
                      'Publish KID Document',
                      <>
                        <strong>
                          Are you sure you want to publish this KID document?
                        </strong>
                        <p className='pb-2'>
                          This will publish the KID document for share
                          class {row.original.share_class_name_full || row.original.share_class_name || row.original.share_class_id}.
                        </p>
                        <b className='text-red-500'>This action cannot be reverted.</b>
                      </>,
                      () => {
                        const payload: KidDocumentIdentifierPayload = {
                          client_id: clientId,
                          fund_id: fund.fund_id,
                          share_class_id: row.original.share_class_id,
                          document_language: row.original.document_language,
                        };
                        publishKidDocumentMutation.mutateAsync(payload);
                      },
                    );
                  }}
                />
                <IconButton variant="download" tooltip="Download latest Published" onClick={() => {
                  fetchLatestPublishedVersionInformation(row.original);
                }} />
                <PortalTools
                  toolBarBtns={[
                    <IconButton
                      variant="delete"
                      label="Deactivate Document"
                      onClick={() => {
                        handleConfirmation(
                          'Deactivate Document',
                          <>
                            <strong>
                              Are you sure you want to deactivate this KID
                              document?
                            </strong>
                            <p>
                              This action will deactivate the KID document for
                              share class {row.original.share_class_name_full || row.original.share_class_name || row.original.share_class_id}. You can
                              reactivate it later if needed.
                            </p>
                          </>,
                          () => {
                            const payload = {
                              client_id: clientId,
                              fund_id: fund.fund_id,
                              share_class_id: row.original.share_class_id,
                              document_language: row.original.document_language,
                            };

                            deactivateKidDocumentMutation.mutateAsync(payload);
                          },
                        );
                      }}
                    />,
                  ]}
                />
              </>
            );
          };

          return (
            <div className="flex items-center justify-center">
              <div className="flex w-[160px] items-center justify-between space-x-1">
                <div className="flex items-center space-x-1">
                  {renderButtons()}
                </div>
              </div>
            </div>
          );
        },
      },
    ],
    [
      fund.fund_id,
      clientId,
      navigate,
      setCreateOpen,
      setSelectedShareClass,
      deactivateKidDocumentMutation,
      reactivateKidDocumentMutation,
      publishKidDocumentMutation,
      handleConfirmation,
      fetchLatestPublishedVersionInformation,
    ],
  );


  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
      columnFilters,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
  } as any);

  // Scroll to and briefly highlight the target share class when returning from editor/review/compare
  useEffect(() => {
    if (!highlightShareClassId) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(
        `[data-share-class-id="${highlightShareClassId}"]`,
      );
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedId(highlightShareClassId);
      setTimeout(() => setHighlightedId(null), 2000);
    }, 200);
    return () => clearTimeout(timer);
  }, [highlightShareClassId]);

  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <div className="rounded-sm border border-black/30 bg-slate-50">
          <div className="p-2">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-mainblue">
                Share Class Details
              </h3>

              <div className="flex items-center space-x-4">
                {/* 🔍 Global Search */}
                <SearchBar
                  searchHandler={setGlobalFilter}
                  placeholder="Search shareclasses..."
                />

                {/* 🟢 Status Filter */}
                <select
                  value={
                    columnFilters.find((f) => f.id === 'status')?.value ?? ''
                  }
                  onChange={(e) => {
                    const value = e.target.value || undefined;
                    setColumnFilters((prev) =>
                      prev
                        .filter((f) => f.id !== 'status')
                        .concat(value ? [{ id: 'status', value }] : []),
                    );
                  }}
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                >
                  <option value="">All Status</option>
                  <option value="published">Published</option>
                  <option value="reviewed">Ready to Publish</option>
                  <option value="editing">Editing</option>
                  <option value="no-document">No Document</option>
                  <option value="deactivated">Deactivated</option>
                </select>

                {/* ☐ Show Deactivated */}
                <label className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showDeactivated}
                    onChange={(e) => setShowDeactivated(e.target.checked)}
                    className="rounded"
                  />
                  <span>Show Deactivated</span>
                </label>
              </div>
            </div>

            <div className="overflow-x-auto rounded border border-gray-200 bg-white shadow-sm">
              <table className="w-full">
                <thead className="bg-mainblue text-white">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`px-3 py-2 text-sm font-medium ${(header.column.columnDef.meta as ColumnAlignMeta)?.align === 'left' ? 'text-left' : 'text-center'}`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="bg-white">
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={table.getAllColumns().length}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        No share classes found
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => {
                      const shareClass = row.original as KidShareClass;
                      const isDeactivated =
                        shareClass.status === KidDocumentStatus.Deactivated ||
                        !shareClass.kid_is_active;
                      const isNoDocument = shareClass.status === KidDocumentStatus.NoDocument;
                      const isFaded = isDeactivated || isNoDocument;
                      return (
                        <tr
                          key={row.id}
                          data-share-class-id={shareClass.share_class_id}
                          className={`border-b border-gray-100 transition-colors duration-700 ${highlightedId === shareClass.share_class_id
                            ? 'bg-blue-100'
                            : isFaded
                              ? 'bg-gray-50 text-gray-400'
                              : 'bg-white hover:bg-gray-50'
                            }`}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className={`px-3 py-2 text-xs ${(cell.column.columnDef.meta as ColumnAlignMeta)?.align === 'left' ? 'text-left' : 'text-center'}`}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {selectedShareClass && (
          <CreateKidDocumentModal
            isOpen={createOpen}
            onClose={() => setCreateOpen(false)}
            shareClassId={selectedShareClass.share_class_id}
            shareClassName={selectedShareClass.share_class_isin}
            fundId={fund.fund_id}
            fundName={fund.fund_name_full}
            jurisdiction={selectedJurisdiction}
            language={selectedLanguage}
          />
        )}
      </td>
    </tr>
  );
};

// Component to handle review navigation with version parameters
const ReviewButton: FunctionComponent<{
  shareClass: KidShareClass;
  fundId: string;
  clientId: string;
  disabled: boolean;
  hasNotification: boolean;
  tooltip?: string;
}> = ({ shareClass, fundId, clientId, disabled, hasNotification, tooltip = 'Review Document' }) => {
  const navigate = useNavigate();
  const hasDocument = shareClass.status !== KidDocumentStatus.NoDocument;

  // Fetch historical data to get versions
  const { data: historicalData } = useGetKidOverviewHistoricalData(
    clientId,
    fundId,
    shareClass.share_class_id,
    shareClass.document_language,
    {
      enabled:
        !disabled &&
        hasDocument &&
        !!clientId &&
        !!fundId &&
        !!shareClass.share_class_id,
    },
  );

  const handleReviewClick = () => {
    const params = getReviewVersionParams(historicalData);
    const search = new URLSearchParams({
      fundId,
      shareClassId: shareClass.share_class_id,
      documentLanguage: shareClass.document_language || 'English',
    });
    if (params) {
      search.set('targetVersion', params.targetVersion);
      search.set('compareVersion', params.compareVersion);
    }
    navigate(`${RaidrV2RoutePaths.KID_REVIEW_V2}?${search.toString()}`);
  };

  return (
    <IconButton
      variant="review"
      onClick={handleReviewClick}
      disabled={disabled}
      hasNotification={hasNotification}
      tooltip={tooltip}
    />
  );
};

export default ShareClassTableRow;
